import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Snackbar,
  Avatar,
  IconButton,
} from '@mui/material';
import {
  CloudUpload,
  Delete,
  Image as ImageIcon,
} from '@mui/icons-material';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { config } from '../config/environment';

const SystemSettings: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [logoPath, setLogoPath] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  const isPmpAdmin = () => {
    if (!user?.roles) return false;
    return user.roles.some(role => role.slug === 'pmp_admin');
  };

  useEffect(() => {
    if (!isPmpAdmin()) {
      setSnackbar({ open: true, message: 'Only PMP admins can access system settings', severity: 'error' });
      return;
    }
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/system-settings');
      if (response.data.success) {
        setLogoUrl(response.data.data.logo_url);
        setLogoPath(response.data.data.logo_path);
      }
    } catch (error: any) {
      console.error('Error fetching system settings:', error);
      setSnackbar({ open: true, message: 'Failed to load system settings', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setSnackbar({ open: true, message: 'Please select an image file', severity: 'error' });
      return;
    }

    // Validate file size (2MB max)
    if (file.size > 2 * 1024 * 1024) {
      setSnackbar({ open: true, message: 'File size must be less than 2MB', severity: 'error' });
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('logo', file);

      const response = await api.post('/system-settings/logo', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success) {
        setLogoUrl(response.data.data.logo_url);
        setLogoPath(response.data.data.logo_path);
        setSnackbar({ open: true, message: 'Logo uploaded successfully', severity: 'success' });
      }
    } catch (error: any) {
      console.error('Error uploading logo:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to upload logo',
        severity: 'error'
      });
    } finally {
      setUploading(false);
      // Reset file input
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  const handleDeleteLogo = async () => {
    if (!window.confirm('Are you sure you want to delete the logo?')) {
      return;
    }

    try {
      setDeleting(true);
      const response = await api.delete('/system-settings/logo');

      if (response.data.success) {
        setLogoUrl(null);
        setLogoPath(null);
        setSnackbar({ open: true, message: 'Logo deleted successfully', severity: 'success' });
      }
    } catch (error: any) {
      console.error('Error deleting logo:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to delete logo',
        severity: 'error'
      });
    } finally {
      setDeleting(false);
    }
  };

  if (!isPmpAdmin()) {
    return (
      <Container maxWidth="md">
        <Paper sx={{ p: 3, mt: 3 }}>
          <Alert severity="error">Only PMP admins can access system settings</Alert>
        </Paper>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="md">
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h5" gutterBottom>
          System Settings
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Manage system-wide settings including logo upload
        </Typography>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Logo
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Upload a logo to display in the application header and sidebar. Recommended size: 200x50px or similar aspect ratio. Max file size: 2MB. Supported formats: JPEG, PNG, GIF, SVG.
          </Typography>

          {logoUrl ? (
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Avatar
                  src={logoUrl}
                  alt="Logo"
                  variant="rounded"
                  sx={{
                    width: 200,
                    height: 'auto',
                    maxHeight: 80,
                    bgcolor: 'transparent',
                  }}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<CloudUpload />}
                  disabled={uploading}
                >
                  Replace Logo
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleLogoUpload}
                  />
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<Delete />}
                  onClick={handleDeleteLogo}
                  disabled={deleting}
                >
                  {deleting ? 'Deleting...' : 'Delete Logo'}
                </Button>
              </Box>
            </Box>
          ) : (
            <Box>
              <Button
                variant="outlined"
                component="label"
                startIcon={<ImageIcon />}
                disabled={uploading}
              >
                {uploading ? 'Uploading...' : 'Upload Logo'}
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleLogoUpload}
                />
              </Button>
            </Box>
          )}
        </Box>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default SystemSettings;

